import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  Brain,
  Plus,
  Search,
  Filter,
  Edit3,
  Trash2,
  Archive,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  AlertTriangle,
  Tag,
  Globe,
  Building,
  ChevronDown,
  ChevronRight,
  Shield,
  Zap,
  Target,
  FileText,
  Copy,
  ToggleLeft,
  ToggleRight,
  Loader2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../components/ui/dropdown-menu';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// Enums matching backend
const TRAINING_TYPES = [
  { value: 'method_example', label: 'Method Example', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' },
  { value: 'rule_guidance', label: 'Rule Guidance', color: 'bg-amber-500/10 text-amber-400 border-amber-500/30' },
  { value: 'packaging_guidance', label: 'Packaging Guidance', color: 'bg-teal-500/10 text-teal-400 border-teal-500/30' },
  { value: 'exclusion_condition', label: 'Exclusion Condition', color: 'bg-red-500/10 text-red-400 border-red-500/30' },
  { value: 'inquiry_handling', label: 'Inquiry Handling', color: 'bg-purple-500/10 text-purple-400 border-purple-500/30' },
  { value: 'personal_info_handling', label: 'Personal Info Handling', color: 'bg-pink-500/10 text-pink-400 border-pink-500/30' },
  { value: 'governance_guidance', label: 'Governance Guidance', color: 'bg-orange-500/10 text-orange-400 border-orange-500/30' },
  { value: 'letter_strategy', label: 'Letter Strategy', color: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30' },
  { value: 'bureau_targeting', label: 'Bureau Targeting', color: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/30' },
  { value: 'round_escalation', label: 'Round Escalation', color: 'bg-violet-500/10 text-violet-400 border-violet-500/30' },
];

const TRAINING_STATUSES = [
  { value: 'draft', label: 'Draft', color: 'bg-slate-500/10 text-slate-400 border-slate-500/30', icon: Clock },
  { value: 'active', label: 'Active', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
  { value: 'inactive', label: 'Inactive', color: 'bg-amber-500/10 text-amber-400 border-amber-500/30', icon: XCircle },
  { value: 'archived', label: 'Archived', color: 'bg-slate-600/10 text-slate-500 border-slate-600/30', icon: Archive },
];

const SCOPES = [
  { value: 'global', label: 'Global', icon: Globe },
  { value: 'tenant', label: 'Tenant', icon: Building },
];

const DISPUTE_CATEGORIES = [
  { value: 'factual', label: 'Factual' },
  { value: 'data_breach', label: 'Data Breach' },
  { value: 'personal_info_accuracy', label: 'Personal Info Accuracy' },
  { value: 'inquiry', label: 'Inquiry' },
  { value: 'collection', label: 'Collection' },
  { value: 'structural', label: 'Structural' },
  { value: 'recovery', label: 'Recovery' },
  { value: 'isolated', label: 'Isolated' },
  { value: 'metro2', label: 'Metro2' },
  { value: 'debt_validation', label: 'Debt Validation' },
  { value: 'generic', label: 'Generic' },
];

const ITEM_TYPES = [
  { value: 'account', label: 'Account' },
  { value: 'inquiry', label: 'Inquiry' },
  { value: 'personal_info', label: 'Personal Info' },
  { value: 'public_record', label: 'Public Record' },
  { value: 'collection', label: 'Collection' },
  { value: 'any', label: 'Any' },
];

const BUREAU_SCOPES = [
  { value: 'all', label: 'All Bureaus' },
  { value: 'transunion', label: 'TransUnion' },
  { value: 'experian', label: 'Experian' },
  { value: 'equifax', label: 'Equifax' },
  { value: 'tu_ex', label: 'TU + EX' },
  { value: 'tu_eq', label: 'TU + EQ' },
  { value: 'ex_eq', label: 'EX + EQ' },
];

const DEFAULT_FORM = {
  scope: 'global',
  tenant_id: '',
  training_type: 'method_example',
  title: '',
  description: '',
  tags: [],
  dispute_category: 'generic',
  item_type: 'any',
  bureau_scope: 'all',
  conditions: [],
  conditions_logic: 'AND',
  example_input: null,
  expected_decision: null,
  expected_output: '',
  guidance_text: '',
  decision_rationale: '',
  recommended_method: '',
  blocked_methods: [],
  template_hint: '',
  priority: 50,
  status: 'draft',
};

const DecisionTrainingPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    status: '',
    training_type: '',
    scope: '',
    dispute_category: '',
    search: '',
  });
  
  // Pagination
  const [pagination, setPagination] = useState({ skip: 0, limit: 25, total: 0 });
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [formData, setFormData] = useState({ ...DEFAULT_FORM });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [tagsInput, setTagsInput] = useState('');
  const [blockedMethodsInput, setBlockedMethodsInput] = useState('');
  
  // Fetch records
  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.training_type) params.append('training_type', filters.training_type);
      if (filters.scope) params.append('scope', filters.scope);
      if (filters.dispute_category) params.append('dispute_category', filters.dispute_category);
      params.append('skip', pagination.skip.toString());
      params.append('limit', pagination.limit.toString());
      
      const response = await axios.get(`${API}/decision-training/records?${params}`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      
      let fetchedRecords = response.data.records || [];
      
      // Apply local search filter
      if (filters.search) {
        const search = filters.search.toLowerCase();
        fetchedRecords = fetchedRecords.filter(r =>
          r.title?.toLowerCase().includes(search) ||
          r.description?.toLowerCase().includes(search) ||
          r.guidance_text?.toLowerCase().includes(search) ||
          r.tags?.some(t => t.toLowerCase().includes(search))
        );
      }
      
      setRecords(fetchedRecords);
      setPagination(prev => ({ ...prev, total: response.data.total || 0 }));
    } catch (error) {
      console.error('Failed to fetch records:', error);
      toast.error('Failed to load decision training records');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.skip, pagination.limit, getAuthHeaders]);
  
  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/decision-training/stats`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [getAuthHeaders]);
  
  useEffect(() => {
    fetchRecords();
    fetchStats();
  }, [fetchRecords, fetchStats]);
  
  // Handle create
  const handleCreate = async () => {
    if (!formData.title.trim()) {
      setFormErrors({ title: 'Title is required' });
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        tags: tagsInput ? tagsInput.split(',').map(t => t.trim()).filter(Boolean) : [],
        blocked_methods: blockedMethodsInput ? blockedMethodsInput.split(',').map(m => m.trim()).filter(Boolean) : [],
      };
      
      await axios.post(`${API}/decision-training/records`, payload, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      
      toast.success('Decision training record created');
      setShowCreateDialog(false);
      setFormData({ ...DEFAULT_FORM });
      setTagsInput('');
      setBlockedMethodsInput('');
      fetchRecords();
      fetchStats();
    } catch (error) {
      console.error('Failed to create record:', error);
      toast.error(error.response?.data?.detail || 'Failed to create record');
    } finally {
      setSaving(false);
    }
  };
  
  // Handle update
  const handleUpdate = async () => {
    if (!editingRecord || !formData.title.trim()) {
      setFormErrors({ title: 'Title is required' });
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        tags: tagsInput ? tagsInput.split(',').map(t => t.trim()).filter(Boolean) : [],
        blocked_methods: blockedMethodsInput ? blockedMethodsInput.split(',').map(m => m.trim()).filter(Boolean) : [],
      };
      
      await axios.put(`${API}/decision-training/records/${editingRecord.id}`, payload, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      
      toast.success('Record updated');
      setEditingRecord(null);
      setFormData({ ...DEFAULT_FORM });
      setTagsInput('');
      setBlockedMethodsInput('');
      fetchRecords();
      fetchStats();
    } catch (error) {
      console.error('Failed to update record:', error);
      toast.error(error.response?.data?.detail || 'Failed to update record');
    } finally {
      setSaving(false);
    }
  };
  
  // Handle status toggle (activate/deactivate)
  const handleStatusToggle = async (record) => {
    const newStatus = record.status === 'active' ? 'inactive' : 'active';
    try {
      await axios.put(`${API}/decision-training/records/${record.id}`, { status: newStatus }, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      toast.success(`Record ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchRecords();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };
  
  // Handle archive (soft delete)
  const handleArchive = async (record) => {
    try {
      await axios.delete(`${API}/decision-training/records/${record.id}`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      toast.success('Record archived');
      fetchRecords();
      fetchStats();
    } catch (error) {
      toast.error('Failed to archive record');
    }
  };
  
  // Open edit dialog
  const openEditDialog = (record) => {
    setEditingRecord(record);
    setFormData({
      scope: record.scope || 'global',
      tenant_id: record.tenant_id || '',
      training_type: record.training_type || 'method_example',
      title: record.title || '',
      description: record.description || '',
      dispute_category: record.dispute_category || 'generic',
      item_type: record.item_type || 'any',
      bureau_scope: record.bureau_scope || 'all',
      conditions: record.conditions || [],
      conditions_logic: record.conditions_logic || 'AND',
      example_input: record.example_input || null,
      expected_decision: record.expected_decision || null,
      expected_output: record.expected_output || '',
      guidance_text: record.guidance_text || '',
      decision_rationale: record.decision_rationale || '',
      recommended_method: record.recommended_method || '',
      template_hint: record.template_hint || '',
      priority: record.priority ?? 50,
      status: record.status || 'draft',
    });
    setTagsInput((record.tags || []).join(', '));
    setBlockedMethodsInput((record.blocked_methods || []).join(', '));
    setFormErrors({});
  };
  
  // Get status info
  const getStatusInfo = (status) => TRAINING_STATUSES.find(s => s.value === status) || TRAINING_STATUSES[0];
  const getTypeInfo = (type) => TRAINING_TYPES.find(t => t.value === type) || TRAINING_TYPES[0];
  const getScopeInfo = (scope) => SCOPES.find(s => s.value === scope) || SCOPES[0];
  
  // Check admin access
  const ADMIN_ROLES = ['platform_owner', 'platform_admin', 'admin', 'owner', 'specialist'];
  if (!ADMIN_ROLES.includes(user?.role)) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Shield size={48} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-slate-400">Admin access required.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6" data-testid="decision-training-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Brain className="text-amber-500" size={28} />
            Decision Training
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Manage decision engine training records for dispute resolution
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => { fetchRecords(); fetchStats(); }} data-testid="refresh-btn">
            <RefreshCw size={16} className="mr-2" />
            Refresh
          </Button>
          <Button onClick={() => { setShowCreateDialog(true); setFormData({ ...DEFAULT_FORM }); setTagsInput(''); setBlockedMethodsInput(''); }} data-testid="create-record-btn">
            <Plus size={16} className="mr-2" />
            New Record
          </Button>
        </div>
      </div>
      
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-stone-800/50 border border-slate-700 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-white">{stats.total_records || 0}</p>
            <p className="text-xs text-slate-400">Total Records</p>
          </div>
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-emerald-400">{stats.by_status?.active || 0}</p>
            <p className="text-xs text-emerald-400/70">Active</p>
          </div>
          <div className="bg-slate-500/10 border border-slate-500/30 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-slate-400">{stats.by_status?.draft || 0}</p>
            <p className="text-xs text-slate-400/70">Draft</p>
          </div>
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-amber-400">{stats.by_status?.inactive || 0}</p>
            <p className="text-xs text-amber-400/70">Inactive</p>
          </div>
          <div className="bg-stone-700/50 border border-slate-600 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-slate-500">{stats.by_status?.archived || 0}</p>
            <p className="text-xs text-slate-500/70">Archived</p>
          </div>
        </div>
      )}
      
      {/* Filters */}
      <div className="bg-stone-800/50 border border-slate-700 rounded-xl p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input
                placeholder="Search records..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10 bg-stone-900 border-slate-700 text-white"
                data-testid="search-input"
              />
            </div>
          </div>
          <Select value={filters.status || 'all'} onValueChange={(v) => setFilters(prev => ({ ...prev, status: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-[150px] bg-stone-900 border-slate-700 text-white" data-testid="filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-stone-800 border-slate-700">
              <SelectItem value="all">All Statuses</SelectItem>
              {TRAINING_STATUSES.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.training_type || 'all'} onValueChange={(v) => setFilters(prev => ({ ...prev, training_type: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-[180px] bg-stone-900 border-slate-700 text-white" data-testid="filter-type">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent className="bg-stone-800 border-slate-700">
              <SelectItem value="all">All Types</SelectItem>
              {TRAINING_TYPES.map(t => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.scope || 'all'} onValueChange={(v) => setFilters(prev => ({ ...prev, scope: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-[140px] bg-stone-900 border-slate-700 text-white" data-testid="filter-scope">
              <SelectValue placeholder="Scope" />
            </SelectTrigger>
            <SelectContent className="bg-stone-800 border-slate-700">
              <SelectItem value="all">All Scopes</SelectItem>
              {SCOPES.map(s => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.dispute_category || 'all'} onValueChange={(v) => setFilters(prev => ({ ...prev, dispute_category: v === 'all' ? '' : v }))}>
            <SelectTrigger className="w-[160px] bg-stone-900 border-slate-700 text-white" data-testid="filter-category">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent className="bg-stone-800 border-slate-700">
              <SelectItem value="all">All Categories</SelectItem>
              {DISPUTE_CATEGORIES.map(c => (
                <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(filters.status || filters.training_type || filters.scope || filters.dispute_category || filters.search) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFilters({ status: '', training_type: '', scope: '', dispute_category: '', search: '' })}
              className="text-slate-400 hover:text-white"
            >
              Clear Filters
            </Button>
          )}
        </div>
      </div>
      
      {/* Records List */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24 bg-stone-800" />)}
        </div>
      ) : records.length === 0 ? (
        <div className="text-center py-16 bg-stone-800/30 border border-slate-700 rounded-xl">
          <Brain size={48} className="mx-auto text-slate-600 mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No Training Records</h3>
          <p className="text-slate-400 mb-4">Create your first decision training record to get started.</p>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-first-btn">
            <Plus size={16} className="mr-2" />
            Create Record
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {records.map((record) => {
            const statusInfo = getStatusInfo(record.status);
            const typeInfo = getTypeInfo(record.training_type);
            const scopeInfo = getScopeInfo(record.scope);
            const StatusIcon = statusInfo.icon;
            const ScopeIcon = scopeInfo.icon;
            const isActive = record.status === 'active';
            
            return (
              <div
                key={record.id}
                className={`bg-stone-800/50 border rounded-xl p-4 transition-all hover:border-amber-500/30 ${
                  isActive ? 'border-emerald-500/30' : 'border-slate-700'
                }`}
                data-testid={`record-${record.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h3 className="font-semibold text-white truncate">{record.title}</h3>
                      <span className={`px-2 py-0.5 text-xs rounded border ${statusInfo.color}`}>
                        <StatusIcon size={12} className="inline mr-1" />
                        {statusInfo.label}
                      </span>
                      <span className={`px-2 py-0.5 text-xs rounded border ${typeInfo.color}`}>
                        {typeInfo.label}
                      </span>
                      <span className="px-2 py-0.5 text-xs rounded border border-slate-600 text-slate-400">
                        <ScopeIcon size={12} className="inline mr-1" />
                        {scopeInfo.label}
                        {record.tenant_id && record.scope === 'tenant' && (
                          <span className="ml-1 text-slate-500">({record.tenant_id})</span>
                        )}
                      </span>
                      <span className="text-xs text-slate-500">Priority: {record.priority}</span>
                    </div>
                    
                    {record.description && (
                      <p className="text-sm text-slate-400 mb-2 line-clamp-1">{record.description}</p>
                    )}
                    
                    {record.guidance_text && (
                      <p className="text-sm text-slate-500 mb-2 line-clamp-2 italic">"{record.guidance_text}"</p>
                    )}
                    
                    <div className="flex items-center gap-4 text-xs text-slate-500 flex-wrap">
                      <span>Category: {record.dispute_category}</span>
                      <span>Item Type: {record.item_type}</span>
                      <span>Bureau: {record.bureau_scope}</span>
                      {record.recommended_method && (
                        <span className="text-emerald-400">Method: {record.recommended_method}</span>
                      )}
                      {record.tags?.length > 0 && (
                        <div className="flex items-center gap-1">
                          <Tag size={10} />
                          {record.tags.slice(0, 3).map((tag, i) => (
                            <span key={i} className="px-1.5 py-0.5 bg-slate-700 rounded text-[10px]">{tag}</span>
                          ))}
                          {record.tags.length > 3 && <span>+{record.tags.length - 3}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {/* Active toggle */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleStatusToggle(record)}
                      className={isActive ? 'text-emerald-400 hover:bg-emerald-500/10' : 'text-slate-400 hover:bg-slate-700'}
                      title={isActive ? 'Deactivate' : 'Activate'}
                      data-testid={`toggle-${record.id}`}
                    >
                      {isActive ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                    </Button>
                    
                    {/* View */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setViewingRecord(record)}
                      className="text-slate-400 hover:text-white"
                      data-testid={`view-${record.id}`}
                    >
                      <Eye size={16} />
                    </Button>
                    
                    {/* Edit */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(record)}
                      className="text-slate-400 hover:text-white"
                      data-testid={`edit-${record.id}`}
                    >
                      <Edit3 size={16} />
                    </Button>
                    
                    {/* More actions */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-slate-400">
                          <ChevronDown size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-stone-800 border-slate-700">
                        <DropdownMenuItem onClick={() => { navigator.clipboard.writeText(record.id); toast.success('ID copied'); }}>
                          <Copy size={14} className="mr-2" />
                          Copy ID
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-slate-700" />
                        <DropdownMenuItem
                          onClick={() => handleArchive(record)}
                          className="text-red-400 focus:text-red-400 focus:bg-red-500/10"
                        >
                          <Archive size={14} className="mr-2" />
                          Archive
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {/* Pagination */}
      {pagination.total > pagination.limit && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-400">
            Showing {pagination.skip + 1}-{Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.skip === 0}
              onClick={() => setPagination(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }))}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pagination.skip + pagination.limit >= pagination.total}
              onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
            >
              Next
            </Button>
          </div>
        </div>
      )}
      
      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog || !!editingRecord} onOpenChange={(open) => { if (!open) { setShowCreateDialog(false); setEditingRecord(null); } }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-stone-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingRecord ? 'Edit Decision Training Record' : 'Create Decision Training Record'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingRecord ? 'Update the training record fields below.' : 'Add a new training record for the decision engine.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label className="text-white">Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Data Breach Method - Inquiry Dispute"
                  className="bg-stone-800 border-slate-700 text-white"
                  data-testid="form-title"
                />
                {formErrors.title && <p className="text-xs text-red-400">{formErrors.title}</p>}
              </div>
              
              <div className="space-y-2">
                <Label className="text-white">Training Type</Label>
                <Select value={formData.training_type} onValueChange={(v) => setFormData(prev => ({ ...prev, training_type: v }))}>
                  <SelectTrigger className="bg-stone-800 border-slate-700 text-white" data-testid="form-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-slate-700">
                    {TRAINING_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-white">Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger className="bg-stone-800 border-slate-700 text-white" data-testid="form-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-slate-700">
                    {TRAINING_STATUSES.filter(s => s.value !== 'archived').map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Scope */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Scope</Label>
                <Select value={formData.scope} onValueChange={(v) => setFormData(prev => ({ ...prev, scope: v }))}>
                  <SelectTrigger className="bg-stone-800 border-slate-700 text-white" data-testid="form-scope">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-slate-700">
                    {SCOPES.map(s => (
                      <SelectItem key={s.value} value={s.value}>
                        <div className="flex items-center gap-2">
                          <s.icon size={14} />
                          {s.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {formData.scope === 'tenant' && (
                <div className="space-y-2">
                  <Label className="text-white">Tenant ID</Label>
                  <Input
                    value={formData.tenant_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, tenant_id: e.target.value }))}
                    placeholder="Enter tenant ID"
                    className="bg-stone-800 border-slate-700 text-white"
                    data-testid="form-tenant-id"
                  />
                </div>
              )}
              
              <div className="space-y-2">
                <Label className="text-white">Priority (0-100)</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 50 }))}
                  className="bg-stone-800 border-slate-700 text-white"
                  data-testid="form-priority"
                />
              </div>
            </div>
            
            {/* Classification */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Dispute Category</Label>
                <Select value={formData.dispute_category} onValueChange={(v) => setFormData(prev => ({ ...prev, dispute_category: v }))}>
                  <SelectTrigger className="bg-stone-800 border-slate-700 text-white" data-testid="form-dispute-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-slate-700">
                    {DISPUTE_CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-white">Item Type</Label>
                <Select value={formData.item_type} onValueChange={(v) => setFormData(prev => ({ ...prev, item_type: v }))}>
                  <SelectTrigger className="bg-stone-800 border-slate-700 text-white" data-testid="form-item-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-slate-700">
                    {ITEM_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label className="text-white">Bureau Scope</Label>
                <Select value={formData.bureau_scope} onValueChange={(v) => setFormData(prev => ({ ...prev, bureau_scope: v }))}>
                  <SelectTrigger className="bg-stone-800 border-slate-700 text-white" data-testid="form-bureau-scope">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-slate-700">
                    {BUREAU_SCOPES.map(b => (
                      <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Description */}
            <div className="space-y-2">
              <Label className="text-white">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of this training record..."
                className="bg-stone-800 border-slate-700 text-white min-h-[80px]"
                data-testid="form-description"
              />
            </div>
            
            {/* Guidance Text */}
            <div className="space-y-2">
              <Label className="text-white">Guidance Text</Label>
              <Textarea
                value={formData.guidance_text}
                onChange={(e) => setFormData(prev => ({ ...prev, guidance_text: e.target.value }))}
                placeholder="Detailed guidance for the decision engine..."
                className="bg-stone-800 border-slate-700 text-white min-h-[100px]"
                data-testid="form-guidance"
              />
            </div>
            
            {/* Method Hints */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Recommended Method</Label>
                <Input
                  value={formData.recommended_method}
                  onChange={(e) => setFormData(prev => ({ ...prev, recommended_method: e.target.value }))}
                  placeholder="e.g., data_breach_method"
                  className="bg-stone-800 border-slate-700 text-white"
                  data-testid="form-recommended-method"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-white">Blocked Methods (comma-separated)</Label>
                <Input
                  value={blockedMethodsInput}
                  onChange={(e) => setBlockedMethodsInput(e.target.value)}
                  placeholder="e.g., factual_inaccuracy, structural"
                  className="bg-stone-800 border-slate-700 text-white"
                  data-testid="form-blocked-methods"
                />
              </div>
            </div>
            
            {/* Tags */}
            <div className="space-y-2">
              <Label className="text-white">Tags (comma-separated)</Label>
              <Input
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="e.g., inquiry, data_breach, high_priority"
                className="bg-stone-800 border-slate-700 text-white"
                data-testid="form-tags"
              />
            </div>
            
            {/* Production Notice */}
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
              <p className="text-xs text-amber-400 flex items-start gap-2">
                <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                Only <strong>Active</strong> records influence live decision engine behavior. Draft/Inactive records are for testing only.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); setEditingRecord(null); }} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={editingRecord ? handleUpdate : handleCreate} disabled={saving} data-testid="form-submit">
              {saving && <Loader2 size={16} className="mr-2 animate-spin" />}
              {editingRecord ? 'Update Record' : 'Create Record'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* View Dialog */}
      <Dialog open={!!viewingRecord} onOpenChange={(open) => { if (!open) setViewingRecord(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-stone-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Eye size={18} />
              {viewingRecord?.title}
            </DialogTitle>
          </DialogHeader>
          
          {viewingRecord && (
            <div className="space-y-4 py-4">
              <div className="flex flex-wrap gap-2">
                <span className={`px-2 py-1 text-xs rounded border ${getStatusInfo(viewingRecord.status).color}`}>
                  {getStatusInfo(viewingRecord.status).label}
                </span>
                <span className={`px-2 py-1 text-xs rounded border ${getTypeInfo(viewingRecord.training_type).color}`}>
                  {getTypeInfo(viewingRecord.training_type).label}
                </span>
                <span className="px-2 py-1 text-xs rounded border border-slate-600 text-slate-400">
                  {getScopeInfo(viewingRecord.scope).label}
                </span>
                <span className="px-2 py-1 text-xs rounded border border-slate-600 text-slate-400">
                  Priority: {viewingRecord.priority}
                </span>
              </div>
              
              {viewingRecord.description && (
                <div>
                  <Label className="text-slate-400 text-xs">Description</Label>
                  <p className="text-sm text-white mt-1">{viewingRecord.description}</p>
                </div>
              )}
              
              {viewingRecord.guidance_text && (
                <div className="bg-stone-800 rounded-lg p-3">
                  <Label className="text-slate-400 text-xs">Guidance Text</Label>
                  <p className="text-sm text-slate-300 mt-1 whitespace-pre-wrap">{viewingRecord.guidance_text}</p>
                </div>
              )}
              
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <Label className="text-slate-400 text-xs">Dispute Category</Label>
                  <p className="text-white">{viewingRecord.dispute_category}</p>
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">Item Type</Label>
                  <p className="text-white">{viewingRecord.item_type}</p>
                </div>
                <div>
                  <Label className="text-slate-400 text-xs">Bureau Scope</Label>
                  <p className="text-white">{viewingRecord.bureau_scope}</p>
                </div>
              </div>
              
              {viewingRecord.recommended_method && (
                <div>
                  <Label className="text-slate-400 text-xs">Recommended Method</Label>
                  <p className="text-emerald-400 font-mono text-sm">{viewingRecord.recommended_method}</p>
                </div>
              )}
              
              {viewingRecord.blocked_methods?.length > 0 && (
                <div>
                  <Label className="text-slate-400 text-xs">Blocked Methods</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {viewingRecord.blocked_methods.map((m, i) => (
                      <span key={i} className="px-2 py-0.5 text-xs bg-red-500/20 text-red-400 rounded">{m}</span>
                    ))}
                  </div>
                </div>
              )}
              
              {viewingRecord.tags?.length > 0 && (
                <div>
                  <Label className="text-slate-400 text-xs">Tags</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {viewingRecord.tags.map((tag, i) => (
                      <span key={i} className="px-2 py-0.5 text-xs bg-slate-700 text-slate-300 rounded">{tag}</span>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-4 text-xs text-slate-500 pt-4 border-t border-slate-700">
                <div>
                  <span>Created: {new Date(viewingRecord.created_at).toLocaleString()}</span>
                </div>
                <div>
                  <span>Updated: {new Date(viewingRecord.updated_at).toLocaleString()}</span>
                </div>
                <div>
                  <span>ID: {viewingRecord.id}</span>
                </div>
                <div>
                  <span>Version: {viewingRecord.version || 1}</span>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewingRecord(null)}>Close</Button>
            <Button onClick={() => { openEditDialog(viewingRecord); setViewingRecord(null); }}>
              <Edit3 size={14} className="mr-2" />
              Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DecisionTrainingPage;
